/* Shopify owns consent persistence and regional policy. This file never writes cookies. */
(() => {
  'use strict';
  if (window.NoodoPrivacy) return;

  const methods = {
    analytics: 'analyticsProcessingAllowed',
    marketing: 'marketingAllowed',
    preferences: 'preferencesProcessingAllowed',
    sale_of_data: 'saleOfDataAllowed',
  };
  let pending;
  let reloadScheduled = false;

  function allowed(purposes) {
    const privacy = window.Shopify?.customerPrivacy;
    return purposes.length > 0 && purposes.every((purpose) => {
      try {
        return privacy?.[methods[purpose]]?.() === true;
      } catch (_) {
        return false;
      }
    });
  }

  function notify() {
    document.dispatchEvent(new CustomEvent('noodo:privacy-changed'));
  }

  function ready() {
    if (window.Shopify?.customerPrivacy) return Promise.resolve(window.Shopify.customerPrivacy);
    if (pending) return pending;
    pending = new Promise((resolve, reject) => {
      let retry;
      const timeout = setTimeout(() => {
        clearTimeout(retry);
        reject(new Error('Customer Privacy API unavailable'));
      }, 8000);
      function load() {
        if (typeof window.Shopify?.loadFeatures !== 'function') {
          retry = setTimeout(load, 100);
          return;
        }
        try {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
            clearTimeout(timeout);
            if (error || !window.Shopify.customerPrivacy) {
              reject(error || new Error('Customer Privacy API unavailable'));
              return;
            }
            resolve(window.Shopify.customerPrivacy);
          });
        } catch (error) {
          clearTimeout(timeout);
          reject(error);
        }
      }
      load();
    }).finally(() => { pending = null; });
    return pending;
  }

  // Do not rely on the consent-change event for the initial state.
  document.addEventListener('visitorConsentCollected', notify);
  window.addEventListener('pageshow', () => ready().then(notify).catch(notify));
  window.NoodoPrivacy = Object.freeze({ ready, allowed });

  class PrivacyEmbed extends HTMLElement {
    connectedCallback() {
      this.update = this.update || (() => this.applyConsent());
      document.addEventListener('noodo:privacy-changed', this.update);
      // Custom elements can connect before their children have been parsed.
      queueMicrotask(() => this.applyConsent());
    }

    disconnectedCallback() {
      document.removeEventListener('noodo:privacy-changed', this.update);
    }

    applyConsent() {
      if (!this.isConnected) return;
      const template = this.querySelector(':scope > template');
      const content = this.querySelector(':scope > [data-privacy-content]');
      const placeholder = this.querySelector(':scope > [data-privacy-placeholder]');
      if (!template || !content || !placeholder) return;
      const permitted = allowed((this.dataset.purposes || '').split(/\s+/).filter(Boolean));
      if (!permitted && this.loaded) {
        content.replaceChildren();
        content.hidden = true;
        placeholder.hidden = false;
        this.loaded = false;
        // Removing a widget cannot unload its global JS. Restart with persisted consent.
        if (!reloadScheduled) {
          reloadScheduled = true;
          setTimeout(() => window.location.reload(), 0);
        }
        return;
      }
      if (!permitted || this.loaded || reloadScheduled) return;
      this.loaded = true;
      const fragment = template.content.cloneNode(true);
      // Recreate scripts while disconnected: parser-created and AJAX-inserted
      // templates differ in script activation. Both must execute exactly once.
      // Nested templates remain inert for the theme's own components.
      fragment.querySelectorAll('script').forEach((original) => {
        const script = document.createElement('script');
        for (const attribute of original.attributes) script.setAttribute(attribute.name, attribute.value);
        script.textContent = original.textContent;
        original.replaceWith(script);
      });
      content.appendChild(fragment);
      content.hidden = false;
      placeholder.hidden = true;
    }
  }
  customElements.define('noodo-privacy-embed', PrivacyEmbed);

  const dialog = document.getElementById('noodo-cookie-preferences');
  const form = dialog?.querySelector('form');
  const status = dialog?.querySelector('[data-privacy-status]');
  let busy = false;
  let opener;

  function setBusy(value) {
    busy = value;
    form?.querySelectorAll('[data-privacy-save], input').forEach((input) => { input.disabled = value; });
    form?.setAttribute('aria-busy', String(value));
  }

  async function openPreferences(trigger) {
    if (!dialog || dialog.open) return;
    opener = trigger;
    dialog.showModal();
    status.textContent = dialog.dataset.loading;
    setBusy(true);
    try {
      const privacy = await ready();
      if (!dialog.open) return;
      const consent = privacy.currentVisitorConsent();
      Object.keys(methods).forEach((purpose) => {
        form.elements[purpose].checked = consent[purpose] === 'yes';
      });
      status.textContent = '';
      setBusy(false);
      if (navigator.globalPrivacyControl === true) {
        form.elements.sale_of_data.checked = false;
        form.elements.sale_of_data.disabled = true;
      }
    } catch (_) {
      status.textContent = dialog.dataset.unavailable;
      // Close and reopen to retry. Never present a successful save without Shopify.
    }
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cookie-preferences]');
    if (!trigger || !dialog) return;
    event.preventDefault();
    openPreferences(trigger);
  });
  dialog?.querySelector('[data-privacy-close]').addEventListener('click', () => dialog.close());
  dialog?.addEventListener('close', () => { opener?.focus(); });

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (busy) return;
    const choice = event.submitter?.value;
    const consent = {};
    Object.keys(methods).forEach((purpose) => {
      consent[purpose] = choice === 'reject' ? false : form.elements[purpose].checked;
    });
    // Never override an active browser opt-out signal with a form checkbox.
    if (navigator.globalPrivacyControl === true) consent.sale_of_data = false;
    setBusy(true);
    status.textContent = dialog.dataset.saving;
    try {
      const privacy = await ready();
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Consent save timed out')), 8000);
        try {
          privacy.setTrackingConsent(consent, (result) => {
            clearTimeout(timeout);
            if (result?.error) reject(new Error('Consent save failed'));
            else resolve();
          });
        } catch (error) {
          clearTimeout(timeout);
          reject(error);
        }
      });
      notify();
      dialog.close();
    } catch (_) {
      status.textContent = dialog.dataset.saveError;
    } finally {
      setBusy(false);
      if (navigator.globalPrivacyControl === true) form.elements.sale_of_data.disabled = true;
    }
  });

  ready().then(notify).catch(notify);
})();
